<template>
  <v-card flat class="pa-4">
    <div class="firme-head">
      <span class="fh-title">Firme</span>
      <DxButton text="Adaugă firmă" icon="add" type="default" styling-mode="contained" @click="openAdd" />
    </div>
    <v-divider class="my-3"></v-divider>

    <DxDataGrid
      :data-source="companies"
      key-expr="ID"
      :show-borders="true"
      :show-column-lines="false"
      :show-row-lines="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      @row-prepared="onRowPrepared"
    >
      <DxSearchPanel :visible="true" placeholder="Caută firmă..." />
      <DxPaging :page-size="10" />
      <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 20, 50]" show-info />

      <DxColumn data-field="Name" caption="Denumire" />
      <DxColumn caption="Server" :calculate-cell-value="r => srvOf(r.Source)" />
      <DxColumn caption="Bază de date" :calculate-cell-value="r => dbOf(r.Source)" />
      <DxColumn data-field="Active" caption="Stare" :width="120" alignment="center" cell-template="stareCell" />
      <DxColumn caption="" :width="110" cell-template="actions" />

      <template #stareCell="{ data }">
        <span class="stare-badge" :class="data.data.Active ? 'on' : 'off'">
          {{ data.data.Active ? 'Activă' : 'Inactivă' }}
        </span>
      </template>

      <template #actions="{ data }">
        <div class="row-actions">
          <DxButton icon="edit" styling-mode="text" @click="openEdit(data.data)" />
          <DxButton icon="trash" styling-mode="text" type="danger" @click="remove(data.data)" />
        </div>
      </template>
    </DxDataGrid>

    <!-- Popup adaugare/editare -->
    <DxPopup v-model:visible="popupVisible" :title="isNew ? 'Adaugă firmă' : 'Editează firmă'"
      :width="560" height="auto" :drag-enabled="false" :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="firma-form">
        <div class="ff-row">
          <div class="ff-field ff-full">
            <label>Denumire</label>
            <DxTextBox v-model:value="form.Name" placeholder="Nume companie" />
          </div>
        </div>
        <div class="ff-row">
          <div class="ff-field">
            <label>Server</label>
            <DxTextBox v-model:value="form.Server" placeholder="46.224.3.219" />
          </div>
          <div class="ff-field ff-small">
            <label>Port</label>
            <DxTextBox v-model:value="form.Port" placeholder="1433" />
          </div>
        </div>
        <div class="ff-row">
          <div class="ff-field">
            <label>Bază de date</label>
            <DxTextBox v-model:value="form.Database" placeholder="Initial Catalog" />
          </div>
        </div>
        <div class="ff-row">
          <div class="ff-field">
            <label>Utilizator</label>
            <DxTextBox v-model:value="form.User" placeholder="User ID" />
          </div>
          <div class="ff-field">
            <label>Parolă</label>
            <DxTextBox v-model:value="form.Password" mode="password" placeholder="Password" />
          </div>
        </div>
        <div class="ff-row ff-checks">
          <DxCheckBox v-model:value="form.IntegratedSecurity" text="Integrated Security" />
          <DxCheckBox v-model:value="form.Encrypt" text="Encrypt" />
          <DxCheckBox v-model:value="form.TrustServerCertificate" text="Trust Server Certificate" />
        </div>
        <div class="ff-row ff-checks">
          <DxCheckBox v-model:value="form.Active" text="Firmă activă" />
        </div>

        <div class="ff-preview">
          <label>Connection string</label>
          <code>{{ buildSource() }}</code>
        </div>

        <div v-if="testMsg" class="ff-test" :class="testOk ? 'ok' : 'err'">
          <v-icon size="16">{{ testOk ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span>{{ testMsg }}</span>
        </div>

        <div class="ff-actions">
          <DxButton text="Testează conexiunea" icon="link" styling-mode="outlined"
            :disabled="testing" @click="testConnection" />
          <span class="ff-spacer"></span>
          <DxButton text="Salvează" type="default" styling-mode="contained" @click="save" />
          <DxButton text="Anulează" styling-mode="outlined" @click="popupVisible = false" />
        </div>
      </div>
    </DxPopup>

    <!-- Confirmare stergere -->
    <DxPopup v-model:visible="deleteVisible" title="Confirmare ștergere" :width="420" height="auto"
      :drag-enabled="false" :wrapper-attr="{ class: 'era-edit-popup' }">
      <div class="del-body">
        <p>Sigur vrei să ștergi firma <strong>„{{ toDelete && toDelete.Name }}"</strong>?</p>
        <p class="del-warn">Acțiunea nu poate fi anulată.</p>
        <div class="ff-actions">
          <span class="ff-spacer"></span>
          <DxButton text="Șterge" type="danger" styling-mode="contained" @click="confirmDelete" />
          <DxButton text="Anulează" styling-mode="outlined" @click="deleteVisible = false" />
        </div>
      </div>
    </DxPopup>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from "vue";
import DxDataGrid, { DxColumn, DxPaging, DxPager, DxSearchPanel } from "devextreme-vue/data-grid";
import { DxPopup } from "devextreme-vue/popup";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxCheckBox } from "devextreme-vue/check-box";
import { DxButton } from "devextreme-vue/button";
import notify from "devextreme/ui/notify";
import ADServices from "@/services/AUTH/AD_Services";

interface FirmaForm {
  ID: string | null; Name: string; Active: boolean;
  Server: string; Port: string; Database: string; User: string; Password: string;
  IntegratedSecurity: boolean; Encrypt: boolean; TrustServerCertificate: boolean;
}

const emptyForm = (): FirmaForm => ({
  ID: null, Name: '', Active: true,
  Server: '', Port: '', Database: '', User: '', Password: '',
  IntegratedSecurity: false, Encrypt: false, TrustServerCertificate: false,
});

export default defineComponent({
  name: "ERP_CompaniesGrid",
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxSearchPanel, DxPopup, DxTextBox, DxCheckBox, DxButton },
  setup() {
    const companies = ref<any[]>([]);
    const popupVisible = ref(false);
    const isNew = ref(true);
    const form = reactive<FirmaForm>(emptyForm());
    const testing = ref(false);
    const testMsg = ref("");
    const testOk = ref(false);
    const deleteVisible = ref(false);
    const toDelete = ref<any>(null);

    const load = () => {
      ADServices.getCompanies().then(res => { companies.value = res.data; }).catch(console.error);
    };

    const parseSource = (src: string): Partial<FirmaForm> => {
      const f: any = { Server: '', Port: '', Database: '', User: '', Password: '', IntegratedSecurity: false, Encrypt: false, TrustServerCertificate: false };
      (src || '').split(';').forEach(pair => {
        const idx = pair.indexOf('='); if (idx < 0) return;
        const k = pair.slice(0, idx).trim().toLowerCase();
        const v = pair.slice(idx + 1).trim();
        if (k === 'server' || k === 'data source') { const [h, p] = v.split(','); f.Server = (h || '').trim(); f.Port = (p || '').trim(); }
        else if (k === 'initial catalog' || k === 'database') f.Database = v;
        else if (k === 'user id' || k === 'uid') f.User = v;
        else if (k === 'password' || k === 'pwd') f.Password = v;
        else if (k === 'integrated security') f.IntegratedSecurity = /true|sspi|yes/i.test(v);
        else if (k === 'encrypt') f.Encrypt = /true|yes/i.test(v);
        else if (k === 'trustservercertificate') f.TrustServerCertificate = /true|yes/i.test(v);
      });
      return f;
    };

    const buildSource = (): string => {
      const server = form.Port ? `${form.Server},${form.Port}` : form.Server;
      return `Server=${server};Initial Catalog=${form.Database};Integrated Security=${form.IntegratedSecurity ? 'true' : 'false'};User ID=${form.User};Password=${form.Password};Encrypt=${form.Encrypt ? 'true' : 'false'};TrustServerCertificate=${form.TrustServerCertificate ? 'true' : 'false'};`;
    };

    const srvOf = (src: string) => { const p = parseSource(src) as any; return p.Port ? `${p.Server}:${p.Port}` : p.Server; };
    const dbOf = (src: string) => (parseSource(src) as any).Database;

    const openAdd = () => { Object.assign(form, emptyForm()); testMsg.value = ""; isNew.value = true; popupVisible.value = true; };
    const openEdit = (row: any) => {
      Object.assign(form, emptyForm(), { ID: row.ID, Name: row.Name, Active: !!row.Active }, parseSource(row.Source));
      testMsg.value = ""; isNew.value = false; popupVisible.value = true;
    };

    const testConnection = async () => {
      testing.value = true; testMsg.value = "";
      try {
        const res = await ADServices.testConnection(buildSource());
        testOk.value = !!res.data?.ok;
        testMsg.value = res.data?.message || (testOk.value ? "Conexiune reușită." : "Conexiune eșuată.");
      } catch (e: any) {
        testOk.value = false;
        testMsg.value = e?.response?.data?.message || "Eroare la testarea conexiunii.";
      } finally { testing.value = false; }
    };

    const onRowPrepared = (e: any) => {
      if (e.rowType === "data" && e.data && e.data.Active === false) {
        e.rowElement.classList.add("row-inactive");
      }
    };

    const confirmDelete = async () => {
      const row = toDelete.value; if (!row) return;
      try { await ADServices.deleteCompany(row.ID); notify('Firmă ștearsă.', 'success', 2000); deleteVisible.value = false; load(); }
      catch (e: any) { notify(e?.response?.data?.Message || 'Eroare la ștergere.', 'error', 4000); }
    };

    const save = async () => {
      if (!form.Name?.trim()) { notify('Introduceți denumirea firmei.', 'warning', 3000); return; }
      const payload: any = { Name: form.Name.trim(), Source: buildSource(), Active: form.Active };
      try {
        if (isNew.value) await ADServices.createCompany(payload);
        else { payload.ID = form.ID; await ADServices.updateCompany(form.ID as string, payload); }
        notify('Firmă salvată.', 'success', 2000);
        popupVisible.value = false;
        load();
      } catch (e: any) { notify(e?.response?.data?.Message || 'Eroare la salvare.', 'error', 4000); }
    };

    const remove = (row: any) => { toDelete.value = row; deleteVisible.value = true; };

    onMounted(load);
    return {
      companies, popupVisible, isNew, form, buildSource, srvOf, dbOf, openAdd, openEdit, save, remove,
      testing, testMsg, testOk, testConnection, onRowPrepared, deleteVisible, toDelete, confirmDelete,
    };
  }
});
</script>

<style scoped>
.firme-head { display: flex; align-items: center; justify-content: space-between; }
.fh-title { font-size: 1.1rem; font-weight: 600; color: #1e293b; }
.row-actions { display: flex; gap: 2px; }

.firma-form { display: flex; flex-direction: column; gap: 14px; padding: 4px 2px; }
.ff-row { display: flex; gap: 14px; }
.ff-field { display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 0; }
.ff-field.ff-small { flex: 0 0 110px; }
.ff-field label, .ff-preview label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: #94a3b8; }
.ff-checks { flex-wrap: wrap; gap: 18px; padding-top: 4px; }
.ff-preview { display: flex; flex-direction: column; gap: 5px; background: #f8f9fc; border: 1px solid #f1f5f9; border-radius: 8px; padding: 10px 12px; }
.ff-preview code { font-size: 11.5px; color: #475569; word-break: break-all; line-height: 1.4; }
.ff-actions { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.ff-spacer { flex: 1; }

.ff-test { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 8px 12px; border-radius: 8px; }
.ff-test.ok { background: #ecfdf5; color: #065f46; }
.ff-test.err { background: #fef2f2; color: #b91c1c; }

.stare-badge { font-size: 11px; font-weight: 700; padding: 2px 12px; border-radius: 20px; }
.stare-badge.on { background: #ecfdf5; color: #059669; }
.stare-badge.off { background: #f1f5f9; color: #94a3b8; }

.del-body { display: flex; flex-direction: column; gap: 8px; padding: 6px 2px; font-size: 14px; color: #334155; }
.del-warn { color: #94a3b8; font-size: 12.5px; }
</style>

<style>
/* rand firma inactiva (grid randat fara scope pe randuri) */
.row-inactive td { opacity: 0.55; }
</style>
